import type {
  ArcAdminListArtifact,
  ArcCreateRequest,
  ArcEditRequest,
  ArcForAdminArtifact,
  ArcMutationReceipt,
  ArcParticipationCreateRequest,
  ArcParticipationDeleteReceipt,
  ArcParticipationDeleteRequest,
  ArcParticipationEditRequest,
  ArcParticipationMutationReceipt,
  ArcParticipationValidationError,
  ArcParticipationsAdminListArtifact,
  ArcValidationError,
  EntitiesAdminListArtifact,
  EntitiesArtifact,
  EntityAliasCreateRequest,
  EntityAliasDeleteReceipt,
  EntityAliasDeleteRequest,
  EntityAliasEditRequest,
  EntityAliasMutationReceipt,
  EntityAliasValidationError,
  EntityAliasesAdminListArtifact,
  EntitySocialLinkCreateRequest,
  EntitySocialLinkDeleteReceipt,
  EntitySocialLinkDeleteRequest,
  EntitySocialLinkEditRequest,
  EntitySocialLinkMutationReceipt,
  EntitySocialLinkValidationError,
  EntitySocialLinksAdminListArtifact,
  EntityArcParticipationsAdminListArtifact,
  EntityEditRequest,
  EntityForAdminArtifact,
  EntityMutationReceipt,
  EntityPortraitUploadArtifact,
  EntityValidationError,
  MutationEventsAction,
  MutationEventsAdminQueueArtifact,
  MutationEventsTargetTable,
  ProductionMessageReviewStateChangeReceipt,
  ProductionMessageReviewStateChangeRequest,
  ProductionMessageReviewStateValidationError,
  ProductionMessagesAdminQueueArtifact,
  RecapArtifact,
  RecapEventCreateRequest,
  RecapEventDeleteReceipt,
  RecapEventDeleteRequest,
  RecapEventDeleteValidationError,
  RecapEventEditRequest,
  RecapEventEntityCreateRequest,
  RecapEventEntityDeleteReceipt,
  RecapEventEntityDeleteRequest,
  RecapEventEntityMutationReceipt,
  RecapEventEntityValidationError,
  RecapEventMutationReceipt,
  RecapEventReviewStateChangeReceipt,
  RecapEventReviewStateChangeRequest,
  RecapEventReviewStateValidationError,
  RecapEventValidationError,
  RecapEventsForAdminListArtifact,
  RecapReviewState,
  TtsAdminQueueArtifact,
  TtsEntryDeleteReceipt,
  TtsEntryDeleteRequest,
  TtsEntryDeleteValidationError,
  TtsEntryReviewStateChangeReceipt,
  TtsEntryReviewStateChangeRequest,
  TtsEntryReviewStateValidationError,
  WikiAdminArcsListArtifact,
  WikiAdminChallengesListArtifact,
  WikiAdminListArtifact,
  WikiArcSummaryEditRequest,
  WikiArcSummaryRowSnapshot,
  WikiChallengeSummaryEditRequest,
  WikiChallengeSummaryRowSnapshot,
  WikiEntityPageCreateRequest,
  WikiEntityPageEditRequest,
  WikiMutationReceiptEnvelope,
  WikiPageRowSnapshot,
  WikiPageStatusChangeReceipt,
  WikiPageStatusChangeRequest,
  WikiPageStatusValidationError,
  WikiValidationError,
  XSourceEntriesAdminQueueArtifact,
  XSourceEntryReviewStateChangeReceipt,
  XSourceEntryReviewStateChangeRequest,
  XSourceEntryReviewStateValidationError,
} from '@fishstank/contracts'
import { doAdminReadFetch } from './adminFetch'
import type { FetchResult } from './adminFetch'

// Admin → Worker client (Slice 2 CP1; read-path consolidated CP9 Phase 1).
// Mirrors apps/public/src/lib/worker.ts in shape so callers on both sides
// handle the same discriminated union.
//
// CP9 Phase 1 (A1): the four read helpers delegate to `doAdminReadFetch`
// in ./adminFetch. `FetchResult<T>` is re-exported here so the existing
// `import { type FetchResult } from '../lib/worker'` call sites in
// pages/Events.tsx (and peers) keep resolving without a touch. The
// mutation helpers below (`postRecapEvent`, `patchRecapEvent`) are
// byte-identical to CP8 — not in Phase 1 scope.

export type { FetchResult } from './adminFetch'

const WORKER_URL: string = import.meta.env.VITE_WORKER_URL ?? ''

export async function fetchEntities(): Promise<FetchResult<EntitiesArtifact>> {
  return doAdminReadFetch<EntitiesArtifact>({
    path: '/v2/staging/entities.json',
    label: 'entities.json',
    isEmpty: (a) => a.body.entities.length === 0,
  })
}

// -- Recap (CP3) ---------------------------------------------------------

export interface FetchRecapOptions {
  arcSlug?: string | null
  entitySlug?: string | null
}

/**
 * Admin → Worker recap fetch. Default (no options) calls the active-arc
 * resolution path; the admin surface in Slice 2 CP3 always calls it
 * without options so operators see whichever arc the Worker resolved.
 */
export async function fetchRecap(
  opts: FetchRecapOptions = {},
): Promise<FetchResult<RecapArtifact>> {
  const search = new URLSearchParams()
  if (opts.arcSlug) search.set('arc', opts.arcSlug)
  if (opts.entitySlug) search.set('entity', opts.entitySlug)
  return doAdminReadFetch<RecapArtifact>({
    path: '/v2/staging/recap.json',
    label: 'recap.json',
    isEmpty: (a) => a.body.events.length === 0,
    search,
  })
}

// -- Wiki admin list (CP5) -----------------------------------------------

/**
 * Admin → Worker bounded wiki read. Returns every `wiki_pages` row
 * regardless of status, ordered `updated_at DESC`, capped on the worker
 * side at `ADMIN_WIKI_LIST_CAP` (200). Admin operators need to see
 * drafts / review / archived rows so the public-index "published only"
 * filter does NOT apply here.
 */
export async function fetchWikiAdminList(): Promise<FetchResult<WikiAdminListArtifact>> {
  return doAdminReadFetch<WikiAdminListArtifact>({
    path: '/v2/staging/wiki/admin/list.json',
    label: 'wiki/admin/list.json',
    isEmpty: (a) => a.body.wiki_pages.length === 0,
  })
}

// -- Recap-events admin surface (CP8 Phase 2) ----------------------------
//
// Three helpers:
//   - `fetchRecapEventsAdminList(opts)` — GET /v2/staging/recap-events/admin/list.json
//   - `postRecapEvent(opts)`            — POST /v2/staging/recap-events
//   - `patchRecapEvent(id, opts)`       — PATCH /v2/staging/recap-events/:id
//
// Mutation helpers always attach `X-Fishstank-Admin-Action:
// recap-event-write` and `credentials: 'include'` so the Worker's CSRF
// three-check can pass. The idempotency key is supplied by the caller
// (UI generates `crypto.randomUUID()` per submit) so a retry reuses the
// key and the Worker short-circuits to the prior receipt without
// writing a second audit row.
//
// Errors are modeled with a richer union than the read-path helpers.
// A typed 400 (RecapEventValidationError) is surfaced as
// `kind:'validation_error'` so the UI can render the error inline
// under the offending field without stringifying the shape. A 403 is
// surfaced as `kind:'csrf_error'` so the UI can prompt re-auth rather
// than showing a generic "network error". 404 / 5xx collapse into
// `kind:'error'` with a message.

export type MutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | { kind: 'validation_error'; error: RecapEventValidationError; status: number }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

export interface FetchRecapEventsAdminListOptions {
  arcSlug?: string | null
  page?: number
  /**
   * Day-of-season target. When provided alongside `arcSlug`, the
   * loader resolves it to the page that contains that day's first
   * event and echoes the resolved page in `pagination.page`. Only
   * meaningful with an arc filter.
   */
  jumpToDay?: number | null
}

export async function fetchRecapEventsAdminList(
  opts: FetchRecapEventsAdminListOptions = {},
): Promise<FetchResult<RecapEventsForAdminListArtifact>> {
  const search = new URLSearchParams()
  if (opts.arcSlug) search.set('arc_slug', opts.arcSlug)
  if (opts.page && opts.page > 1) search.set('page', String(opts.page))
  if (opts.jumpToDay && opts.jumpToDay >= 1) {
    search.set('jump_to_day', String(opts.jumpToDay))
  }
  return doAdminReadFetch<RecapEventsForAdminListArtifact>({
    path: '/v2/staging/recap-events/admin/list.json',
    label: 'recap-events admin list',
    isEmpty: (a) => a.body.items.length === 0,
    search,
  })
}

interface MutationErrorBody {
  ok: false
  error:
    | RecapEventValidationError
    | { code: 'csrf_failed'; reason: string }
    | { code: 'not_found' }
    | { code: 'internal'; message: string }
    | { code: 'method_not_allowed' }
}

/**
 * Narrow the Worker's error body shape. The Worker emits the
 * discriminated union above for every non-200 response on the
 * mutation routes; anything else is a generic network error.
 */
function narrowMutationError<T>(status: number, bodyUnknown: unknown): MutationFetchResult<T> {
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status }
    }
    if (err.code === 'not_found') {
      return { kind: 'not_found', status }
    }
    // Any other `code` is a RecapEventValidationError — the contract
    // is a closed union.
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as RecapEventValidationError,
        status,
      }
    }
  }
  return {
    kind: 'error',
    message: `recap-event mutation failed: HTTP ${status}`,
    status,
  }
}

async function parseMutationResponse<T>(res: Response): Promise<MutationFetchResult<T>> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // Non-JSON error bodies (e.g. raw 'Not Found' from the Worker
    // fallback) surface as a generic error.
    return {
      kind: 'error',
      message: `recap-event mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: body as T }
  }
  return narrowMutationError<T>(res.status, body)
}

export interface PostRecapEventOptions {
  body: RecapEventCreateRequest
}

/**
 * Create a recap event. Idempotency key is the caller's responsibility
 * — UI code typically calls `crypto.randomUUID()` once per submit and
 * reuses it across retries so a double-click replays without writing
 * a second audit row.
 */
export async function postRecapEvent(
  opts: PostRecapEventOptions,
): Promise<MutationFetchResult<RecapEventMutationReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/recap-events`
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'recap-event-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseMutationResponse<RecapEventMutationReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Slice 3: recap-event delete ----------------------------------------
//
// Mirrors the arc-participation delete client surface — separate
// `RecapEventDeleteFetchResult<T>` so the validation_error variant
// carries the delete-specific error union (recap_event_not_found,
// invalid_body, invalid_uuid, idempotency_key_in_use) rather than the
// wider create/edit error type. Same DELETE-with-body posture as the
// other admin delete routes; idempotency key is the caller's
// responsibility.

export type RecapEventDeleteFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | {
      kind: 'validation_error'
      error: RecapEventDeleteValidationError
      status: number
    }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

function narrowRecapEventDeleteError<T>(
  status: number,
  bodyUnknown: unknown,
): RecapEventDeleteFetchResult<T> {
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status }
    }
    if (err.code === 'recap_event_not_found' || err.code === 'not_found') {
      return { kind: 'not_found', status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as RecapEventDeleteValidationError,
        status,
      }
    }
  }
  return {
    kind: 'error',
    message: `recap-event delete failed: HTTP ${status}`,
    status,
  }
}

async function parseRecapEventDeleteResponse<T>(
  res: Response,
): Promise<RecapEventDeleteFetchResult<T>> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `recap-event delete failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: body as T }
  }
  return narrowRecapEventDeleteError<T>(res.status, body)
}

export interface DeleteRecapEventOptions {
  body: RecapEventDeleteRequest
}

export async function deleteRecapEvent(
  id: string,
  opts: DeleteRecapEventOptions,
): Promise<RecapEventDeleteFetchResult<RecapEventDeleteReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `deleteRecapEvent: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/recap-events/${id}`
  try {
    const res = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'recap-event-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseRecapEventDeleteResponse<RecapEventDeleteReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

export interface PatchRecapEventOptions {
  body: RecapEventEditRequest
}

/**
 * Edit an existing recap event. Only the fields actually being changed
 * are sent — the UI builds the delta from the prior row snapshot so
 * the Worker's whitelist-strict parser only sees what's intended to
 * flip. Idempotency key works the same way as `postRecapEvent`.
 */
export async function patchRecapEvent(
  id: string,
  opts: PatchRecapEventOptions,
): Promise<MutationFetchResult<RecapEventMutationReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  // PATCH target id is a stringified bigint; the Worker's route regex
  // only accepts `\d+`, so we sanity-check here to fail fast without a
  // network round trip.
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `patchRecapEvent: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/recap-events/${id}`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'recap-event-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseMutationResponse<RecapEventMutationReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Wiki admin surface (CP10 Phase 3) -----------------------------------
//
// Six helpers mirror the recap-event pattern but run against the CP10
// mutation family:
//   - `fetchWikiAdminArcs()`         — GET /v2/staging/wiki/admin/arcs.json
//   - `fetchWikiAdminChallenges()`   — GET /v2/staging/wiki/admin/challenges.json
//   - `createWikiEntityPage(opts)`   — POST  /v2/staging/wiki/entity-pages
//   - `editWikiEntityPage(id, opts)` — PATCH /v2/staging/wiki/entity-pages/:id
//   - `editArcSummary(id, opts)`     — PATCH /v2/staging/wiki/arc-summaries/:id
//   - `editChallengeSummary(id,opts)`— PATCH /v2/staging/wiki/challenge-summaries/:id
//
// Mutation helpers attach the CP10 action labels to
// `X-Fishstank-Admin-Action` so the worker-side three-check passes:
//   - `wiki-entity-page-write`        for create/edit entity pages
//   - `wiki-arc-summary-write`        for arc summary edit
//   - `wiki-challenge-summary-write`  for challenge summary edit
//
// Error shape is identical to CP8 (`{ ok: false, error: { code } }`), but
// the validation-error union is `WikiValidationError` not
// `RecapEventValidationError`. Rather than widen `MutationFetchResult<T>`
// or invent a second discriminant, we keep the existing kind tags and
// define a `WikiMutationFetchResult<T>` that carries the wiki-shaped
// validation error. Call sites narrow on `kind` exactly as in Events.tsx.

export type WikiMutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | { kind: 'validation_error'; error: WikiValidationError; status: number }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

/**
 * Narrow the Worker's error body for CP10 wiki mutation routes. Same
 * discriminated-union body shape as recap but the "other code" branch
 * carries a `WikiValidationError` instead of a `RecapEventValidationError`.
 */
function narrowWikiMutationError<T>(
  status: number,
  bodyUnknown: unknown,
): WikiMutationFetchResult<T> {
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status }
    }
    if (err.code === 'not_found') {
      return { kind: 'not_found', status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as WikiValidationError,
        status,
      }
    }
  }
  return {
    kind: 'error',
    message: `wiki mutation failed: HTTP ${status}`,
    status,
  }
}

async function parseWikiMutationResponse<T>(res: Response): Promise<WikiMutationFetchResult<T>> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `wiki mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: body as T }
  }
  return narrowWikiMutationError<T>(res.status, body)
}

// -- Admin-list reads (arcs + challenges) -------------------------------

/**
 * Admin → Worker bounded arcs-for-admin read. Returns every arc row
 * (ordered `updated_at DESC`), capped at `ADMIN_WIKI_ARCS_LIST_CAP`
 * (200) on the worker side. Used by the admin Wiki page to enumerate
 * arc-summary edit targets.
 */
export async function fetchWikiAdminArcs(): Promise<FetchResult<WikiAdminArcsListArtifact>> {
  return doAdminReadFetch<WikiAdminArcsListArtifact>({
    path: '/v2/staging/wiki/admin/arcs.json',
    label: 'wiki/admin/arcs.json',
    isEmpty: (a) => a.body.arcs.length === 0,
  })
}

/**
 * Admin → Worker bounded challenges-for-admin read. Returns every
 * challenge row (ordered `updated_at DESC`), capped at
 * `ADMIN_WIKI_CHALLENGES_LIST_CAP` (200). Used by the admin Wiki page
 * to enumerate challenge-summary edit targets.
 */
export async function fetchWikiAdminChallenges(): Promise<
  FetchResult<WikiAdminChallengesListArtifact>
> {
  return doAdminReadFetch<WikiAdminChallengesListArtifact>({
    path: '/v2/staging/wiki/admin/challenges.json',
    label: 'wiki/admin/challenges.json',
    isEmpty: (a) => a.body.challenges.length === 0,
  })
}

// -- Wiki entity-page mutations (CP10 Phase 1 path) ---------------------

export interface CreateWikiEntityPageOptions {
  body: WikiEntityPageCreateRequest
}

/**
 * Create a wiki entity page. Caller owns `idempotency_key` — generate
 * it once per form open with `crypto.randomUUID()` and reuse across
 * retries so a double-submit replays without writing a second audit
 * row.
 */
export async function createWikiEntityPage(
  opts: CreateWikiEntityPageOptions,
): Promise<WikiMutationFetchResult<WikiMutationReceiptEnvelope<WikiPageRowSnapshot>>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/wiki/entity-pages`
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'wiki-entity-page-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseWikiMutationResponse<WikiMutationReceiptEnvelope<WikiPageRowSnapshot>>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

export interface EditWikiEntityPageOptions {
  body: WikiEntityPageEditRequest
}

/**
 * Edit an existing wiki entity page. Only the fields actually being
 * changed should be sent — the UI builds the delta from the prior row
 * snapshot so the worker's whitelist-strict parser only sees intended
 * mutations. `idempotency_key` is always required.
 */
export async function editWikiEntityPage(
  id: string,
  opts: EditWikiEntityPageOptions,
): Promise<WikiMutationFetchResult<WikiMutationReceiptEnvelope<WikiPageRowSnapshot>>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `editWikiEntityPage: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/wiki/entity-pages/${id}`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'wiki-entity-page-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseWikiMutationResponse<WikiMutationReceiptEnvelope<WikiPageRowSnapshot>>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Arc and challenge summary mutations (CP10 Phase 2 paths) -----------

export interface EditArcSummaryOptions {
  body: WikiArcSummaryEditRequest
}

/**
 * Edit an arc's `summary_md`. Whitelist-strict at the worker (only
 * `summary_md` is editable; `idempotency_key` is required). Passing
 * `null` clears the column; omitting `summary_md` is rejected as
 * `empty_patch`.
 */
export async function editArcSummary(
  id: string,
  opts: EditArcSummaryOptions,
): Promise<WikiMutationFetchResult<WikiMutationReceiptEnvelope<WikiArcSummaryRowSnapshot>>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `editArcSummary: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/wiki/arc-summaries/${id}`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'wiki-arc-summary-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseWikiMutationResponse<WikiMutationReceiptEnvelope<WikiArcSummaryRowSnapshot>>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

export interface EditChallengeSummaryOptions {
  body: WikiChallengeSummaryEditRequest
}

/**
 * Edit a challenge's `summary_md`. Same whitelist posture as
 * `editArcSummary`.
 */
export async function editChallengeSummary(
  id: string,
  opts: EditChallengeSummaryOptions,
): Promise<WikiMutationFetchResult<WikiMutationReceiptEnvelope<WikiChallengeSummaryRowSnapshot>>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `editChallengeSummary: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/wiki/challenge-summaries/${id}`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'wiki-challenge-summary-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseWikiMutationResponse<WikiMutationReceiptEnvelope<WikiChallengeSummaryRowSnapshot>>(
      res,
    )
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Recap review-state surface (CP11 first slice) ----------------------
//
// One helper:
//   - `patchRecapEventReviewState(id, opts)`
//       — PATCH /v2/staging/recap-events/:id/review-state
//
// Mirrors the CP10 wiki helper pattern: dedicated discriminated-union
// result type so the Events admin UI can narrow on the new error codes
// (`invalid_review_state`, `review_state_unchanged`, `idempotency_conflict`)
// without widening `MutationFetchResult<T>`.
//
// CSRF action label: `recap-event-review-state-write` (matches the
// constant in worker/src/recap-mutations.ts).

export type RecapReviewMutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | {
      kind: 'validation_error'
      error: RecapEventReviewStateValidationError
      status: number
    }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

function narrowRecapReviewMutationError<T>(
  status: number,
  bodyUnknown: unknown,
): RecapReviewMutationFetchResult<T> {
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status }
    }
    if (err.code === 'not_found') {
      return { kind: 'not_found', status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as RecapEventReviewStateValidationError,
        status,
      }
    }
  }
  return {
    kind: 'error',
    message: `recap-event review-state mutation failed: HTTP ${status}`,
    status,
  }
}

async function parseRecapReviewMutationResponse<T>(
  res: Response,
): Promise<RecapReviewMutationFetchResult<T>> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `recap-event review-state mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: body as T }
  }
  return narrowRecapReviewMutationError<T>(res.status, body)
}

export interface PatchRecapEventReviewStateOptions {
  body: RecapEventReviewStateChangeRequest
}

/**
 * Flip a recap_events row's review_state. Idempotency key MUST be a
 * fresh UUID per click — a replay with the same key + same to_state
 * returns the prior receipt (no double-audit), but a same-key replay
 * with a different to_state returns 409 idempotency_conflict.
 */
export async function patchRecapEventReviewState(
  id: string,
  opts: PatchRecapEventReviewStateOptions,
): Promise<RecapReviewMutationFetchResult<RecapEventReviewStateChangeReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `patchRecapEventReviewState: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/recap-events/${id}/review-state`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'recap-event-review-state-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseRecapReviewMutationResponse<RecapEventReviewStateChangeReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Wiki page status surface (CP12 first slice) ------------------------
//
// One helper:
//   - `patchWikiEntityPageStatus(id, opts)`
//       — PATCH /v2/staging/wiki/entity-pages/:id/status
//
// Mirrors `patchRecapEventReviewState` exactly: dedicated
// discriminated-union result type so the Wiki admin UI can narrow on
// the new error codes (`invalid_to_status`, `wiki_page_status_unchanged`,
// `wiki_status_transition_forbidden`,
// `wiki_page_status_idempotency_conflict`) without widening
// `WikiMutationFetchResult<T>`.
//
// CSRF action label: `wiki-page-status-write` (matches the constant in
// worker/src/wiki-mutations.ts).

export type WikiPageStatusMutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | {
      kind: 'validation_error'
      error: WikiPageStatusValidationError
      status: number
    }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

function narrowWikiPageStatusMutationError<T>(
  status: number,
  bodyUnknown: unknown,
): WikiPageStatusMutationFetchResult<T> {
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status }
    }
    if (err.code === 'wiki_page_not_found') {
      return { kind: 'not_found', status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as WikiPageStatusValidationError,
        status,
      }
    }
  }
  return {
    kind: 'error',
    message: `wiki page status mutation failed: HTTP ${status}`,
    status,
  }
}

async function parseWikiPageStatusMutationResponse<T>(
  res: Response,
): Promise<WikiPageStatusMutationFetchResult<T>> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `wiki page status mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: body as T }
  }
  return narrowWikiPageStatusMutationError<T>(res.status, body)
}

export interface PatchWikiEntityPageStatusOptions {
  body: WikiPageStatusChangeRequest
}

/**
 * Flip a wiki_pages row's status. Idempotency key MUST be a fresh
 * UUID per click — a replay with the same key + same to_status
 * returns the prior receipt (no double-audit), but a same-key replay
 * with a different to_status returns 409
 * wiki_page_status_idempotency_conflict.
 */
export async function patchWikiEntityPageStatus(
  id: string,
  opts: PatchWikiEntityPageStatusOptions,
): Promise<WikiPageStatusMutationFetchResult<WikiPageStatusChangeReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `patchWikiEntityPageStatus: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/wiki/entity-pages/${id}/status`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'wiki-page-status-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseWikiPageStatusMutationResponse<WikiPageStatusChangeReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- TTS admin surface (CP13 first slice) -------------------------------
//
// Two helpers:
//   - `fetchTtsAdminQueue(opts?)` — GET /v2/staging/tts/admin/queue.json
//   - `patchTtsEntryReviewState(id, opts)` —
//       PATCH /v2/staging/tts/entries/:id/review-state
//
// Mirrors `patchRecapEventReviewState` exactly: dedicated
// discriminated-union result type so the TTS admin UI can narrow on
// the new error codes (`invalid_review_state`,
// `tts_review_state_unchanged`, `tts_idempotency_conflict`,
// `tts_entry_not_found`) without widening
// `MutationFetchResult<T>`.
//
// CSRF action label: `tts-entry-review-state-write` (matches the
// constant in worker/src/tts-mutations.ts).

export interface FetchTtsAdminQueueOptions {
  page?: number
}

export async function fetchTtsAdminQueue(
  opts: FetchTtsAdminQueueOptions = {},
): Promise<FetchResult<TtsAdminQueueArtifact>> {
  const search = new URLSearchParams()
  if (opts.page && opts.page >= 1) search.set('page', String(opts.page))
  return doAdminReadFetch<TtsAdminQueueArtifact>({
    path: '/v2/staging/tts/admin/queue.json',
    label: 'tts/admin/queue.json',
    isEmpty: (a) => a.body.items.length === 0,
    search,
  })
}

export type TtsReviewMutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | {
      kind: 'validation_error'
      error: TtsEntryReviewStateValidationError
      status: number
    }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

function narrowTtsReviewMutationError<T>(
  status: number,
  bodyUnknown: unknown,
): TtsReviewMutationFetchResult<T> {
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status }
    }
    if (err.code === 'tts_entry_not_found') {
      return { kind: 'not_found', status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as TtsEntryReviewStateValidationError,
        status,
      }
    }
  }
  return {
    kind: 'error',
    message: `tts review-state mutation failed: HTTP ${status}`,
    status,
  }
}

async function parseTtsReviewMutationResponse<T>(
  res: Response,
): Promise<TtsReviewMutationFetchResult<T>> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `tts review-state mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: body as T }
  }
  return narrowTtsReviewMutationError<T>(res.status, body)
}

export interface PatchTtsEntryReviewStateOptions {
  body: TtsEntryReviewStateChangeRequest
}

/**
 * Flip a tts_entries row's review_state. Idempotency key MUST be a
 * fresh UUID per click. Same posture as patchRecapEventReviewState:
 * same-key/same-state replay returns the prior receipt;
 * same-key/different-state returns 409 tts_idempotency_conflict.
 *
 * The Worker also writes through to `tts_entries.hide_reason` per
 * the CP13 rule (set on hide/flag, cleared on approve/unreview); the
 * receipt's `to_hide_reason` echoes the column value actually
 * written.
 */
export async function patchTtsEntryReviewState(
  id: string,
  opts: PatchTtsEntryReviewStateOptions,
): Promise<TtsReviewMutationFetchResult<TtsEntryReviewStateChangeReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `patchTtsEntryReviewState: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/tts/entries/${id}/review-state`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'tts-entry-review-state-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseTtsReviewMutationResponse<TtsEntryReviewStateChangeReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Slice 4: TTS entry hard delete --------------------------------------
//
// Mirrors the slice 3 RecapEvent delete client surface. Separate
// `TtsEntryDeleteFetchResult<T>` so the validation_error variant
// carries the delete-specific error union (tts_entry_not_found,
// invalid_body, invalid_uuid, idempotency_key_in_use) rather than
// the wider review-state error type.
//
// CSRF action label: `tts-entry-write` (matches the constant in
// worker/src/tts-mutations.ts; distinct from the review-state label
// so the two surfaces evolve independently).

export type TtsEntryDeleteFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | {
      kind: 'validation_error'
      error: TtsEntryDeleteValidationError
      status: number
    }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

function narrowTtsEntryDeleteError<T>(
  status: number,
  bodyUnknown: unknown,
): TtsEntryDeleteFetchResult<T> {
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status }
    }
    if (err.code === 'tts_entry_not_found') {
      return { kind: 'not_found', status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as TtsEntryDeleteValidationError,
        status,
      }
    }
  }
  return {
    kind: 'error',
    message: `tts-entry delete failed: HTTP ${status}`,
    status,
  }
}

async function parseTtsEntryDeleteResponse<T>(
  res: Response,
): Promise<TtsEntryDeleteFetchResult<T>> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `tts-entry delete failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: body as T }
  }
  return narrowTtsEntryDeleteError<T>(res.status, body)
}

export interface DeleteTtsEntryOptions {
  body: TtsEntryDeleteRequest
}

export async function deleteTtsEntry(
  id: string,
  opts: DeleteTtsEntryOptions,
): Promise<TtsEntryDeleteFetchResult<TtsEntryDeleteReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `deleteTtsEntry: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/tts/entries/${id}`
  try {
    const res = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'tts-entry-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseTtsEntryDeleteResponse<TtsEntryDeleteReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Production Messages admin surface (CP14 first slice) ----------------
//
// Two helpers:
//   - `fetchProductionMessagesAdminQueue(opts?)` —
//       GET /v2/staging/production-messages/admin/queue.json
//   - `mutateProductionMessageReviewState(id, opts)` —
//       PATCH /v2/staging/production-messages/:id/review-state
//
// Structural clone of the CP13 TTS pair. Deltas:
//   - queue accepts `{ since, review_state, author, page }` filters
//     (TTS queue has only `page`); author is case-preserved (audit P16)
//   - validation-error narrowing uses the `production_message_*`
//     namespaced codes (including hide_reason variants)
//   - CSRF action label: `production-message-review-state-write`
//
// TanStack Query key convention: `['production-messages', 'admin',
// 'queue']` for the list, invalidated on every successful mutation.

export interface FetchProductionMessagesAdminQueueOptions {
  since?: string | null
  review_state?: RecapReviewState | 'any' | null
  author?: string | null
  page?: number
}

export async function fetchProductionMessagesAdminQueue(
  opts: FetchProductionMessagesAdminQueueOptions = {},
): Promise<FetchResult<ProductionMessagesAdminQueueArtifact>> {
  const search = new URLSearchParams()
  if (opts.since) search.set('since', opts.since)
  if (opts.review_state) search.set('review_state', opts.review_state)
  if (opts.author) search.set('author', opts.author)
  if (opts.page && opts.page >= 1) search.set('page', String(opts.page))
  return doAdminReadFetch<ProductionMessagesAdminQueueArtifact>({
    path: '/v2/staging/production-messages/admin/queue.json',
    label: 'production-messages/admin/queue.json',
    isEmpty: (a) => a.body.items.length === 0,
    search,
  })
}

export type ProductionMessageReviewMutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | {
      kind: 'validation_error'
      error: ProductionMessageReviewStateValidationError
      status: number
    }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

function narrowProductionMessageReviewMutationError<T>(
  status: number,
  bodyUnknown: unknown,
): ProductionMessageReviewMutationFetchResult<T> {
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status }
    }
    if (err.code === 'production_message_not_found') {
      return { kind: 'not_found', status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as ProductionMessageReviewStateValidationError,
        status,
      }
    }
  }
  return {
    kind: 'error',
    message: `production-message review-state mutation failed: HTTP ${status}`,
    status,
  }
}

async function parseProductionMessageReviewMutationResponse<T>(
  res: Response,
): Promise<ProductionMessageReviewMutationFetchResult<T>> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `production-message review-state mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: body as T }
  }
  return narrowProductionMessageReviewMutationError<T>(res.status, body)
}

export interface MutateProductionMessageReviewStateOptions {
  body: ProductionMessageReviewStateChangeRequest
}

/**
 * Flip a production_messages row's review_state. Idempotency key MUST
 * be a fresh UUID per click. Same posture as patchTtsEntryReviewState:
 * same-key / same-state replay returns the prior receipt;
 * same-key / different-state returns 409
 * `production_message_idempotency_conflict`.
 *
 * The Worker writes through to `production_messages.hide_reason`
 * (required non-empty on hide/flag, cleared on approve/unreview); the
 * receipt's `to_hide_reason` echoes the column value actually written.
 */
export async function mutateProductionMessageReviewState(
  id: string,
  opts: MutateProductionMessageReviewStateOptions,
): Promise<ProductionMessageReviewMutationFetchResult<ProductionMessageReviewStateChangeReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `mutateProductionMessageReviewState: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/production-messages/${id}/review-state`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'production-message-review-state-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseProductionMessageReviewMutationResponse<ProductionMessageReviewStateChangeReceipt>(
      res,
    )
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- X-source entries admin surface (CP15 first slice) ------------------
//
// Two helpers:
//   - `fetchXSourceEntriesAdminQueue(opts?)` —
//       GET /v2/staging/x-source-entries/admin/queue.json
//   - `mutateXSourceEntryReviewState(id, opts)` —
//       PATCH /v2/staging/x-source-entries/:id/review-state
//
// Structural clone of the CP14 production-messages pair. Deltas:
//   - queue accepts `{ review_state, page }` only — NO `since`, NO
//     `author_handle` in first slice (deferred per audit X-6).
//   - validation-error narrowing uses the `x_source_entry_*`
//     namespaced codes (including hide_reason variants).
//   - CSRF action label: `x-source-entry-review-state-write`.
//
// TanStack Query key convention: `['x-source-entries', 'admin',
// 'queue']` for the list, invalidated on every successful mutation.

export interface FetchXSourceEntriesAdminQueueOptions {
  review_state?: RecapReviewState | 'any' | null
  page?: number
}

export async function fetchXSourceEntriesAdminQueue(
  opts: FetchXSourceEntriesAdminQueueOptions = {},
): Promise<FetchResult<XSourceEntriesAdminQueueArtifact>> {
  const search = new URLSearchParams()
  if (opts.review_state) search.set('review_state', opts.review_state)
  if (opts.page && opts.page >= 1) search.set('page', String(opts.page))
  return doAdminReadFetch<XSourceEntriesAdminQueueArtifact>({
    path: '/v2/staging/x-source-entries/admin/queue.json',
    label: 'x-source-entries/admin/queue.json',
    isEmpty: (a) => a.body.items.length === 0,
    search,
  })
}

export type XSourceEntryReviewMutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | {
      kind: 'validation_error'
      error: XSourceEntryReviewStateValidationError
      status: number
    }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

function narrowXSourceEntryReviewMutationError<T>(
  status: number,
  bodyUnknown: unknown,
): XSourceEntryReviewMutationFetchResult<T> {
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status }
    }
    if (err.code === 'x_source_entry_not_found') {
      return { kind: 'not_found', status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as XSourceEntryReviewStateValidationError,
        status,
      }
    }
  }
  return {
    kind: 'error',
    message: `x-source-entry review-state mutation failed: HTTP ${status}`,
    status,
  }
}

async function parseXSourceEntryReviewMutationResponse<T>(
  res: Response,
): Promise<XSourceEntryReviewMutationFetchResult<T>> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `x-source-entry review-state mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: body as T }
  }
  return narrowXSourceEntryReviewMutationError<T>(res.status, body)
}

export interface MutateXSourceEntryReviewStateOptions {
  body: XSourceEntryReviewStateChangeRequest
}

/**
 * Flip an x_source_entries row's review_state. Idempotency key MUST
 * be a fresh UUID per click. Same posture as
 * mutateProductionMessageReviewState: same-key / same-state replay
 * returns the prior receipt; same-key / different-state returns 409
 * `x_source_entry_idempotency_conflict`.
 *
 * The Worker writes through to `x_source_entries.hide_reason`
 * (required non-empty on hide/flag, cleared on approve/unreview); the
 * receipt's `to_hide_reason` echoes the column value actually written.
 */
export async function mutateXSourceEntryReviewState(
  id: string,
  opts: MutateXSourceEntryReviewStateOptions,
): Promise<XSourceEntryReviewMutationFetchResult<XSourceEntryReviewStateChangeReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `mutateXSourceEntryReviewState: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/x-source-entries/${id}/review-state`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'x-source-entry-review-state-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseXSourceEntryReviewMutationResponse<XSourceEntryReviewStateChangeReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Mutation-events admin audit-history (CP16 first slice) --------------
//
// One helper:
//   - `fetchMutationEventsAdminQueue(opts?)` —
//       GET /v2/staging/mutation-events/admin/queue.json
//
// READ-ONLY. There is no mutation helper for `mutation_events` — the
// table is written only as a side effect of the editorial-lifecycle
// mutation routes (CP10–CP15).
//
// Filter surface: `target_table`, `target_id`, `action`, `page`. All
// are optional; `target_table` and `action` are both closed
// allowlists. Off-allowlist values cause the Worker to return a
// typed 400 with `error.code === 'mutation_events_invalid_*'` — the
// SPA surfaces that under the offending field. Narrowing mirrors the
// review-state helpers.

export interface FetchMutationEventsAdminQueueOptions {
  target_table?: MutationEventsTargetTable | null
  target_id?: string | null
  action?: MutationEventsAction | null
  page?: number
}

export async function fetchMutationEventsAdminQueue(
  opts: FetchMutationEventsAdminQueueOptions = {},
): Promise<FetchResult<MutationEventsAdminQueueArtifact>> {
  const search = new URLSearchParams()
  if (opts.target_table) search.set('target_table', opts.target_table)
  if (opts.target_id) search.set('target_id', opts.target_id)
  if (opts.action) search.set('action', opts.action)
  if (opts.page && opts.page >= 1) search.set('page', String(opts.page))
  return doAdminReadFetch<MutationEventsAdminQueueArtifact>({
    path: '/v2/staging/mutation-events/admin/queue.json',
    label: 'mutation-events/admin/queue.json',
    isEmpty: (a) => a.body.items.length === 0,
    search,
  })
}

// -- Entity admin surface (CP17 Phase 2) ---------------------------------
//
// Three helpers backing the admin SPA's entity edit slice:
//   - `fetchEntitiesForAdmin()`        — GET /v2/staging/entities/admin/list.json
//   - `fetchEntityForAdmin(id)`        — GET /v2/staging/entities/admin/:id.json
//   - `patchEntity(id, opts)`          — PATCH /v2/staging/entities/:id
//
// `patchEntity` attaches `X-Fishstank-Admin-Action: entity-write` and
// `credentials: 'include'` so the Worker's CSRF three-check passes.
// Idempotency key is the caller's responsibility — `EntityEdit.tsx`
// generates `crypto.randomUUID()` once per form open and reuses it
// across retries (double-click safety).
//
// EntityMutationFetchResult mirrors the recap MutationFetchResult shape
// but pins `validation_error` to `EntityValidationError` so the UI can
// render typed inline errors without stringifying.

export type EntityMutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | { kind: 'validation_error'; error: EntityValidationError; status: number }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

export async function fetchEntitiesForAdmin(): Promise<FetchResult<EntitiesAdminListArtifact>> {
  return doAdminReadFetch<EntitiesAdminListArtifact>({
    path: '/v2/staging/entities/admin/list.json',
    label: 'entities/admin/list.json',
    isEmpty: (a) => a.body.entities.length === 0,
  })
}

export async function fetchEntityForAdmin(
  id: string,
): Promise<FetchResult<EntityForAdminArtifact>> {
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `fetchEntityForAdmin: id must be a positive integer string, got '${id}'`,
    }
  }
  return doAdminReadFetch<EntityForAdminArtifact>({
    path: `/v2/staging/entities/admin/${id}.json`,
    label: `entities/admin/${id}.json`,
    isEmpty: () => false,
  })
}

export interface PatchEntityOptions {
  body: EntityEditRequest
}

async function parseEntityMutationResponse<T>(
  res: Response,
): Promise<EntityMutationFetchResult<T>> {
  let bodyUnknown: unknown = null
  try {
    bodyUnknown = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `entity mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: bodyUnknown as T }
  }
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status: res.status }
    }
    if (err.code === 'entity_not_found') {
      return { kind: 'not_found', status: res.status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as EntityValidationError,
        status: res.status,
      }
    }
  }
  return {
    kind: 'error',
    message: `entity mutation failed: HTTP ${res.status}`,
    status: res.status,
  }
}

/**
 * Edit an existing entity. Whitelist-strict — the Worker's parser only
 * accepts primary_name / slug / kind / status. Sending `status:'merged'`
 * surfaces as a `validation_error` with code
 * `merged_status_not_editable`; the UI renders that inline under the
 * status select.
 *
 * Same idempotency posture as recap/wiki: caller generates one UUID
 * per form open and reuses it across retries so a double-click replays
 * to the prior receipt instead of writing a second audit row.
 */
export async function patchEntity(
  id: string,
  opts: PatchEntityOptions,
): Promise<EntityMutationFetchResult<EntityMutationReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `patchEntity: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/entities/${id}`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'entity-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseEntityMutationResponse<EntityMutationReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Entity portrait upload (Slice 1 of entity-portraits) ----------------
//
// Two-step admin flow:
//   1. uploadEntityPortrait(entityId, file) → multipart POST to
//      /v2/staging/entities/<id>/portrait. CSRF action label is
//      `entity-portrait-write`. Width/height are extracted client-side
//      via the File API + Image() and posted alongside the file.
//   2. patchEntity(entityId, { body: { avatar_asset_id: <id> | null }})
//      to actually attach (or clear) the asset. Two-step keeps a
//      failed upload from dirtying the entity row.

export type EntityPortraitUploadFetchResult =
  | { kind: 'ok'; artifact: EntityPortraitUploadArtifact }
  | {
      kind: 'validation_error'
      error: { code: string; field?: string; message?: string }
      status: number
    }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

export interface UploadEntityPortraitOptions {
  /** Pre-extracted natural width — admin reads it via Image(). */
  width: number
  /** Pre-extracted natural height — admin reads it via Image(). */
  height: number
  /** Optional alt text. Trimmed; empty becomes null server-side. */
  alt_text?: string | null
}

export async function uploadEntityPortrait(
  entityId: string,
  file: File,
  opts: UploadEntityPortraitOptions,
): Promise<EntityPortraitUploadFetchResult> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(entityId)) {
    return {
      kind: 'error',
      message: `uploadEntityPortrait: id must be a positive integer string, got '${entityId}'`,
    }
  }

  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/entities/${entityId}/portrait`
  const form = new FormData()
  form.set('file', file)
  form.set('width', String(opts.width))
  form.set('height', String(opts.height))
  if (opts.alt_text && opts.alt_text.trim().length > 0) {
    form.set('alt_text', opts.alt_text.trim())
  }

  let res: Response
  try {
    res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        // No content-type header — browsers set it (with boundary)
        // for FormData. Setting it manually breaks the multipart parse.
        'x-fishstank-admin-action': 'entity-portrait-write',
      },
      body: form,
    })
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }

  // Mirror parseEntityMutationResponse's branching, with the upload
  // route's specific status codes.
  if (res.status === 200) {
    try {
      const artifact = (await res.json()) as EntityPortraitUploadArtifact
      return { kind: 'ok', artifact }
    } catch (err) {
      return {
        kind: 'error',
        message: err instanceof Error ? err.message : 'invalid JSON',
        status: 200,
      }
    }
  }
  if (res.status === 403) {
    try {
      const body = (await res.json()) as {
        ok: false
        error: { code: 'csrf_failed'; reason: string }
      }
      return {
        kind: 'csrf_error',
        reason: body.error.reason,
        status: 403,
      }
    } catch {
      return { kind: 'error', message: 'csrf_failed', status: 403 }
    }
  }
  if (res.status === 404) {
    return { kind: 'not_found', status: 404 }
  }
  if (res.status === 400 || res.status === 413) {
    try {
      const body = (await res.json()) as {
        ok: false
        error: { code: string; field?: string; message?: string }
      }
      return {
        kind: 'validation_error',
        error: body.error,
        status: res.status,
      }
    } catch {
      return { kind: 'error', message: 'validation_error', status: res.status }
    }
  }
  return {
    kind: 'error',
    message: `HTTP ${res.status}`,
    status: res.status,
  }
}

// -- Arcs admin surface (Refinement Phase A1) ----------------------------
//
// One helper backing the new top-level `/arcs` admin page that
// replaces the `Arcs (soon)` shell placeholder:
//   - `fetchArcsForAdmin()` — GET /v2/staging/arcs/admin/list.json
//
// Read-only first slice. Mutation helpers (arc create / arc full-row
// edit) land in subsequent refinement phases per
// FISHSTANK_V2_ARCS_ENTITIES_REFINEMENT_PLAN.md §2 Phase A2/A3.

export async function fetchArcsForAdmin(): Promise<FetchResult<ArcAdminListArtifact>> {
  return doAdminReadFetch<ArcAdminListArtifact>({
    path: '/v2/staging/arcs/admin/list.json',
    label: 'arcs/admin/list.json',
    isEmpty: (a) => a.body.arcs.length === 0,
  })
}

// -- Arc create mutation (Refinement Phase A2) ---------------------------
//
// Single helper:
//   - `postArc(opts)` — POST /v2/staging/arcs
//
// Mirrors the CP8 recap-event-create + CP17 entity-edit posture.
// CSRF action label: `arc-write` (so A3's edit/delete reuse it).
// Idempotency key is the caller's responsibility — the form generates
// `crypto.randomUUID()` once per open and reuses it across retries
// (double-click safety / replay-stable).

export type ArcMutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | { kind: 'validation_error'; error: ArcValidationError; status: number }
  | { kind: 'csrf_error'; reason: string; status: number }
  // Refinement Phase A3 — additive `not_found` branch for the edit
  // surface. PATCH `/v2/staging/arcs/:id` returns 404 with
  // `error.code = 'arc_not_found'`; the SPA narrows on `kind` to
  // render a form-level "this arc no longer exists" banner.
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

export interface PostArcOptions {
  body: ArcCreateRequest
}

async function parseArcMutationResponse<T>(
  res: Response,
): Promise<ArcMutationFetchResult<T>> {
  let bodyUnknown: unknown = null
  try {
    bodyUnknown = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `arc mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: bodyUnknown as T }
  }
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status: res.status }
    }
    // Refinement Phase A3 — narrow `arc_not_found` to its own kind
    // so the SPA renders a form-level "this arc no longer exists"
    // banner instead of a generic field-level validation error.
    if (err.code === 'arc_not_found') {
      return { kind: 'not_found', status: res.status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as ArcValidationError,
        status: res.status,
      }
    }
  }
  return {
    kind: 'error',
    message: `arc mutation failed: HTTP ${res.status}`,
    status: res.status,
  }
}

/**
 * Create an arc. Whitelist-strict — the Worker's parser only accepts
 * slug / title / arc_type / status / idempotency_key.
 *
 * Same idempotency posture as recap/entity: caller generates one
 * UUID per form open and reuses it across retries so a double-click
 * replays to the prior receipt instead of writing a second audit row.
 */
export async function postArc(
  opts: PostArcOptions,
): Promise<ArcMutationFetchResult<ArcMutationReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/arcs`
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'arc-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseArcMutationResponse<ArcMutationReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Arc edit (Refinement Phase A3) --------------------------------------
//
// Two helpers extending the A2 surface:
//   - `fetchArcForAdmin(id)` — GET /v2/staging/arcs/admin/:id.json
//   - `patchArc(id, opts)`   — PATCH /v2/staging/arcs/:id
//
// Reuses the A2 ArcMutationFetchResult discriminated union (now
// extended with the `not_found` branch for the edit path's 404 case).
// CSRF action label is the existing 'arc-write' (shared with create).
//
// Idempotency posture matches A2 + CP17: caller generates one
// crypto.randomUUID() per form open and reuses it across retries so
// double-click replays to the prior receipt instead of writing a
// second audit row.

export async function fetchArcForAdmin(
  id: string,
): Promise<FetchResult<ArcForAdminArtifact>> {
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `fetchArcForAdmin: id must be a positive integer string, got '${id}'`,
    }
  }
  return doAdminReadFetch<ArcForAdminArtifact>({
    path: `/v2/staging/arcs/admin/${id}.json`,
    label: `arcs/admin/${id}.json`,
    isEmpty: () => false,
  })
}

export interface PatchArcOptions {
  body: ArcEditRequest
}

/**
 * Edit an arc. Whitelist-strict — the Worker's parser only accepts
 * slug / title / arc_type / status / display_order / start_date /
 * end_date / idempotency_key. Sending `winner_entity_id`, `summary_md`,
 * or any other field is silently ignored.
 *
 * Same idempotency posture as postArc: caller generates one UUID
 * per form open and reuses it across retries.
 */
export async function patchArc(
  id: string,
  opts: PatchArcOptions,
): Promise<ArcMutationFetchResult<ArcMutationReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `patchArc: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/arcs/${id}`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'arc-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseArcMutationResponse<ArcMutationReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Arc participations admin (Refinement Phase B1) ----------------------
//
// One helper backing the new read-only Participations section on
// the existing /arcs/:id/edit page:
//   - `fetchArcParticipationsForAdmin(arcId)` —
//       GET /v2/staging/arcs/admin/:id/participations.json
//
// Read-only first slice. Mutation helpers (POST/PATCH/DELETE) land
// in B2/B3 per the FISHSTANK_V2_PHASE_B_LINKAGE_PLAN.md sequence.
// The 404 case (arc id doesn't resolve) surfaces as
// `kind:'error'` with status=404 — the SPA's section handles it
// the same way as any other failed read.

export async function fetchArcParticipationsForAdmin(
  arcId: string,
): Promise<FetchResult<ArcParticipationsAdminListArtifact>> {
  if (!/^\d+$/.test(arcId)) {
    return {
      kind: 'error',
      message: `fetchArcParticipationsForAdmin: arcId must be a positive integer string, got '${arcId}'`,
    }
  }
  return doAdminReadFetch<ArcParticipationsAdminListArtifact>({
    path: `/v2/staging/arcs/admin/${arcId}/participations.json`,
    label: `arcs/admin/${arcId}/participations.json`,
    isEmpty: (a) => a.body.participations.length === 0,
  })
}

// -- Entity-side participations admin (Refinement Phase B4) --------------
//
// Mirror of `fetchArcParticipationsForAdmin` anchored on the entity
// instead of the arc. Backs the new EntityParticipationsSection on
// the existing /entities/:id/edit page.
//
//   - `fetchEntityParticipationsForAdmin(entityId)` —
//       GET /v2/staging/entities/admin/:id/participations.json
//
// Read-only. The 404 case (entity id doesn't resolve) surfaces as
// `kind:'error'` with status=404, mirroring B1's posture.

export async function fetchEntityParticipationsForAdmin(
  entityId: string,
): Promise<FetchResult<EntityArcParticipationsAdminListArtifact>> {
  if (!/^\d+$/.test(entityId)) {
    return {
      kind: 'error',
      message: `fetchEntityParticipationsForAdmin: entityId must be a positive integer string, got '${entityId}'`,
    }
  }
  return doAdminReadFetch<EntityArcParticipationsAdminListArtifact>({
    path: `/v2/staging/entities/admin/${entityId}/participations.json`,
    label: `entities/admin/${entityId}/participations.json`,
    isEmpty: (a) => a.body.participations.length === 0,
  })
}

// -- Entity aliases admin (Refinement Phase D) ---------------------------
//
// Four helpers backing the new EntityAliasesSection on /entities/:id/edit:
//   - `fetchEntityAliasesForAdmin(entityId)` — GET (read)
//   - `postEntityAlias(opts)`                — POST (create)
//   - `patchEntityAlias(id, opts)`           — PATCH (edit)
//   - `deleteEntityAlias(id, opts)`          — DELETE (delete)
//
// CSRF action label: 'entity-alias-write' (new label, distinct
// from CP17's 'entity-write' so the alias surface evolves
// independently). Used by all three mutation endpoints.
//
// Idempotency posture matches every CP8+ create surface: caller
// generates one crypto.randomUUID() per form open and reuses it
// across retries (double-click safety, replay-stable). Cross-shape
// collision (delete-key replayed on create/edit, or vice-versa)
// returns typed `idempotency_key_in_use`.

export async function fetchEntityAliasesForAdmin(
  entityId: string,
): Promise<FetchResult<EntityAliasesAdminListArtifact>> {
  if (!/^\d+$/.test(entityId)) {
    return {
      kind: 'error',
      message: `fetchEntityAliasesForAdmin: entityId must be a positive integer string, got '${entityId}'`,
    }
  }
  return doAdminReadFetch<EntityAliasesAdminListArtifact>({
    path: `/v2/staging/entities/admin/${entityId}/aliases.json`,
    label: `entities/admin/${entityId}/aliases.json`,
    isEmpty: (a) => a.body.aliases.length === 0,
  })
}

export type EntityAliasMutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | {
      kind: 'validation_error'
      error: EntityAliasValidationError
      status: number
    }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

async function parseEntityAliasMutationResponse<T>(
  res: Response,
): Promise<EntityAliasMutationFetchResult<T>> {
  let bodyUnknown: unknown = null
  try {
    bodyUnknown = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `entity-alias mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: bodyUnknown as T }
  }
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status: res.status }
    }
    if (err.code === 'alias_not_found') {
      return { kind: 'not_found', status: res.status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as EntityAliasValidationError,
        status: res.status,
      }
    }
  }
  return {
    kind: 'error',
    message: `entity-alias mutation failed: HTTP ${res.status}`,
    status: res.status,
  }
}

export interface PostEntityAliasOptions {
  body: EntityAliasCreateRequest
}

export async function postEntityAlias(
  opts: PostEntityAliasOptions,
): Promise<EntityAliasMutationFetchResult<EntityAliasMutationReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/entity-aliases`
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'entity-alias-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseEntityAliasMutationResponse<EntityAliasMutationReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

export interface PatchEntityAliasOptions {
  body: EntityAliasEditRequest
}

export async function patchEntityAlias(
  id: string,
  opts: PatchEntityAliasOptions,
): Promise<EntityAliasMutationFetchResult<EntityAliasMutationReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `patchEntityAlias: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/entity-aliases/${id}`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'entity-alias-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseEntityAliasMutationResponse<EntityAliasMutationReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

export interface DeleteEntityAliasOptions {
  body: EntityAliasDeleteRequest
}

export async function deleteEntityAlias(
  id: string,
  opts: DeleteEntityAliasOptions,
): Promise<EntityAliasMutationFetchResult<EntityAliasDeleteReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `deleteEntityAlias: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/entity-aliases/${id}`
  try {
    const res = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'entity-alias-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseEntityAliasMutationResponse<EntityAliasDeleteReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Entity social-links admin surface -----------------------------------
//
// Four helpers backing the new EntitySocialLinksSection on
// /entities/:id/edit. Mirrors the entity-aliases pattern one-for-one.
//
//   - fetchEntitySocialLinksForAdmin(entityId) — GET (read)
//   - postEntitySocialLink(opts)               — POST (create)
//   - patchEntitySocialLink(id, opts)          — PATCH (edit)
//   - deleteEntitySocialLink(id, opts)         — DELETE (delete)
//
// CSRF action label: 'entity-social-link-write' (distinct from
// 'entity-write' / 'entity-alias-write' so this surface evolves
// independently). Used by all three mutation endpoints.

export async function fetchEntitySocialLinksForAdmin(
  entityId: string,
): Promise<FetchResult<EntitySocialLinksAdminListArtifact>> {
  if (!/^\d+$/.test(entityId)) {
    return {
      kind: 'error',
      message: `fetchEntitySocialLinksForAdmin: entityId must be a positive integer string, got '${entityId}'`,
    }
  }
  return doAdminReadFetch<EntitySocialLinksAdminListArtifact>({
    path: `/v2/staging/entities/admin/${entityId}/social-links.json`,
    label: `entities/admin/${entityId}/social-links.json`,
    isEmpty: (a) => a.body.social_links.length === 0,
  })
}

export type EntitySocialLinkMutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | {
      kind: 'validation_error'
      error: EntitySocialLinkValidationError
      status: number
    }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

async function parseEntitySocialLinkMutationResponse<T>(
  res: Response,
): Promise<EntitySocialLinkMutationFetchResult<T>> {
  let bodyUnknown: unknown = null
  try {
    bodyUnknown = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `entity-social-link mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: bodyUnknown as T }
  }
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status: res.status }
    }
    if (err.code === 'social_link_not_found') {
      return { kind: 'not_found', status: res.status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as EntitySocialLinkValidationError,
        status: res.status,
      }
    }
  }
  return {
    kind: 'error',
    message: `entity-social-link mutation failed: HTTP ${res.status}`,
    status: res.status,
  }
}

export interface PostEntitySocialLinkOptions {
  body: EntitySocialLinkCreateRequest
}

export async function postEntitySocialLink(
  opts: PostEntitySocialLinkOptions,
): Promise<
  EntitySocialLinkMutationFetchResult<EntitySocialLinkMutationReceipt>
> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/entity-social-links`
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'entity-social-link-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseEntitySocialLinkMutationResponse<EntitySocialLinkMutationReceipt>(
      res,
    )
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

export interface PatchEntitySocialLinkOptions {
  body: EntitySocialLinkEditRequest
}

export async function patchEntitySocialLink(
  id: string,
  opts: PatchEntitySocialLinkOptions,
): Promise<
  EntitySocialLinkMutationFetchResult<EntitySocialLinkMutationReceipt>
> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `patchEntitySocialLink: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/entity-social-links/${id}`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'entity-social-link-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseEntitySocialLinkMutationResponse<EntitySocialLinkMutationReceipt>(
      res,
    )
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

export interface DeleteEntitySocialLinkOptions {
  body: EntitySocialLinkDeleteRequest
}

export async function deleteEntitySocialLink(
  id: string,
  opts: DeleteEntitySocialLinkOptions,
): Promise<
  EntitySocialLinkMutationFetchResult<EntitySocialLinkDeleteReceipt>
> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `deleteEntitySocialLink: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/entity-social-links/${id}`
  try {
    const res = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'entity-social-link-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseEntitySocialLinkMutationResponse<EntitySocialLinkDeleteReceipt>(
      res,
    )
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Recap-event entity attachment admin write ---------------------------
//
// Two helpers backing the new EventEntitiesPanel inside EditEventForm
// on /events:
//   - `postRecapEventEntity(opts)`         — POST   /v2/staging/recap-event-entities
//   - `deleteRecapEventEntity(id, opts)`   — DELETE /v2/staging/recap-event-entities/:id
//
// CSRF action label: 'recap-event-entity-write' (distinct from
// 'recap-event-write' so attachment-mutate stays separate from
// recap-event editing).
//
// Idempotency posture matches every CP8+ create surface — caller
// generates one crypto.randomUUID() per form open and reuses it
// across retries (double-click safety, replay-stable).

export type RecapEventEntityMutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | {
      kind: 'validation_error'
      error: RecapEventEntityValidationError
      status: number
    }
  | { kind: 'csrf_error'; reason: string; status: number }
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

async function parseRecapEventEntityMutationResponse<T>(
  res: Response,
): Promise<RecapEventEntityMutationFetchResult<T>> {
  let bodyUnknown: unknown = null
  try {
    bodyUnknown = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `recap-event-entity mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: bodyUnknown as T }
  }
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status: res.status }
    }
    if (err.code === 'attachment_not_found') {
      return { kind: 'not_found', status: res.status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as RecapEventEntityValidationError,
        status: res.status,
      }
    }
  }
  return {
    kind: 'error',
    message: `recap-event-entity mutation failed: HTTP ${res.status}`,
    status: res.status,
  }
}

export interface PostRecapEventEntityOptions {
  body: RecapEventEntityCreateRequest
}

export async function postRecapEventEntity(
  opts: PostRecapEventEntityOptions,
): Promise<
  RecapEventEntityMutationFetchResult<RecapEventEntityMutationReceipt>
> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/recap-event-entities`
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'recap-event-entity-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseRecapEventEntityMutationResponse<RecapEventEntityMutationReceipt>(
      res,
    )
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

export interface DeleteRecapEventEntityOptions {
  body: RecapEventEntityDeleteRequest
}

export async function deleteRecapEventEntity(
  id: string,
  opts: DeleteRecapEventEntityOptions,
): Promise<
  RecapEventEntityMutationFetchResult<RecapEventEntityDeleteReceipt>
> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `deleteRecapEventEntity: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/recap-event-entities/${id}`
  try {
    const res = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'recap-event-entity-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseRecapEventEntityMutationResponse<RecapEventEntityDeleteReceipt>(
      res,
    )
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Arc participations admin write (Refinement Phase B2) ----------------
//
// One helper backing the new "Add participant" form inside the existing
// ParticipationsSection on /arcs/:id/edit:
//   - `postArcParticipation(opts)` — POST /v2/staging/arc-participations
//
// CSRF action label: 'arc-participation-write' (new label, distinct
// from A2/A3's 'arc-write' so the participations surface evolves
// independently).
//
// Idempotency posture matches every CP8+ create surface: caller
// generates one crypto.randomUUID() per form open and reuses it
// across retries (double-click safety, replay-stable).
//
// 'winner' caveat: B2 ACCEPTS status='winner' but does NOT update
// arcs.winner_entity_id. The form's status help text should spell
// this out plainly — see the SPA's AddParticipationForm.

export type ArcParticipationMutationFetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | {
      kind: 'validation_error'
      error: ArcParticipationValidationError
      status: number
    }
  | { kind: 'csrf_error'; reason: string; status: number }
  // Refinement Phase B3 — additive `not_found` branch for the
  // edit + delete surfaces. PATCH/DELETE on a non-existent
  // participation returns 404 with `error.code='participation_not_found'`.
  | { kind: 'not_found'; status: number }
  | { kind: 'error'; message: string; status?: number }

export interface PostArcParticipationOptions {
  body: ArcParticipationCreateRequest
}

async function parseArcParticipationMutationResponse<T>(
  res: Response,
): Promise<ArcParticipationMutationFetchResult<T>> {
  let bodyUnknown: unknown = null
  try {
    bodyUnknown = await res.json()
  } catch {
    return {
      kind: 'error',
      message: `arc-participation mutation failed: HTTP ${res.status} ${res.statusText}`,
      status: res.status,
    }
  }
  if (res.ok) {
    return { kind: 'ok', artifact: bodyUnknown as T }
  }
  if (
    bodyUnknown &&
    typeof bodyUnknown === 'object' &&
    'error' in (bodyUnknown as Record<string, unknown>) &&
    typeof (bodyUnknown as { error: unknown }).error === 'object' &&
    (bodyUnknown as { error: unknown }).error !== null
  ) {
    const err = (bodyUnknown as { error: { code?: string } }).error
    if (err.code === 'csrf_failed') {
      const r = (err as { reason?: string }).reason ?? ''
      return { kind: 'csrf_error', reason: r, status: res.status }
    }
    // Refinement Phase B3 — narrow `participation_not_found` to
    // its own kind so the SPA renders a form-level "this row no
    // longer exists" banner instead of a generic field error.
    if (err.code === 'participation_not_found') {
      return { kind: 'not_found', status: res.status }
    }
    if (err.code) {
      return {
        kind: 'validation_error',
        error: err as ArcParticipationValidationError,
        status: res.status,
      }
    }
  }
  return {
    kind: 'error',
    message: `arc-participation mutation failed: HTTP ${res.status}`,
    status: res.status,
  }
}

export async function postArcParticipation(
  opts: PostArcParticipationOptions,
): Promise<ArcParticipationMutationFetchResult<ArcParticipationMutationReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/arc-participations`
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'arc-participation-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseArcParticipationMutationResponse<ArcParticipationMutationReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

// -- Arc participations admin edit + delete (Refinement Phase B3) --------
//
// Two helpers extending B2's create surface:
//   - `patchArcParticipation(id, opts)` — PATCH /v2/staging/arc-participations/:id
//   - `deleteArcParticipation(id, opts)` — DELETE /v2/staging/arc-participations/:id
//
// Reuses B2's CSRF label ('arc-participation-write') and the
// existing parseArcParticipationMutationResponse narrower (which
// in B3 maps `participation_not_found` → `kind:'not_found'`).
//
// Idempotency posture matches every CP8+ surface: caller
// generates one crypto.randomUUID() per form open and reuses it
// across retries. Cross-shape collision (delete-key replayed on
// edit, or vice-versa) returns typed `idempotency_key_in_use`.

export interface PatchArcParticipationOptions {
  body: ArcParticipationEditRequest
}

export async function patchArcParticipation(
  id: string,
  opts: PatchArcParticipationOptions,
): Promise<ArcParticipationMutationFetchResult<ArcParticipationMutationReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `patchArcParticipation: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/arc-participations/${id}`
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'arc-participation-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseArcParticipationMutationResponse<ArcParticipationMutationReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}

export interface DeleteArcParticipationOptions {
  body: ArcParticipationDeleteRequest
}

export async function deleteArcParticipation(
  id: string,
  opts: DeleteArcParticipationOptions,
): Promise<ArcParticipationMutationFetchResult<ArcParticipationDeleteReceipt>> {
  if (!WORKER_URL) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  if (!/^\d+$/.test(id)) {
    return {
      kind: 'error',
      message: `deleteArcParticipation: id must be a positive integer string, got '${id}'`,
    }
  }
  const url = `${WORKER_URL.replace(/\/$/, '')}/v2/staging/arc-participations/${id}`
  try {
    const res = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-fishstank-admin-action': 'arc-participation-write',
      },
      body: JSON.stringify(opts.body),
    })
    return parseArcParticipationMutationResponse<ArcParticipationDeleteReceipt>(res)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}
